/**
 * Which email addresses a review ask may go to (k10 "disposable or role address handling"). Pure, so the app can
 * give the answer as the owner types and the API gives the same one again before anything is stored.
 *
 * Refused:
 * - an address that isn't one;
 * - a throwaway inbox (a known disposable domain): the customer gave a made-up address, and mail to it bounces or
 *   lands with nobody, which costs the sending reputation every client shares;
 * - a mailbox nobody reads (noreply@, postmaster@, abuse@ and the like).
 *
 * Allowed on purpose: shared mailboxes such as info@, office@ and accounts@. A landlord, a shop or a property
 * manager is a real customer and that's often the only address they give.
 */

export type AddressVerdict = 'ok' | 'bad_email' | 'throwaway' | 'no_reply_address';

/** Lowercased and trimmed, or null when it can't be an address. Stricter than the storage rule on purpose. */
export function normaliseAddress(input: string): string | null {
  const e = String(input ?? '').trim().toLowerCase();
  if (e.length < 6 || e.length > 254) return null;
  // One @, a local part without spaces or brackets, a domain of dot-separated labels and a letters-only ending.
  const m = /^([a-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64})@((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63})$/.exec(e);
  if (!m) return null;
  const local = m[1]!;
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return null;
  return e;
}

/** Local parts that are never a person. Matched whole, after dots, dashes and underscores are taken out. */
const NOBODY = new Set([
  'noreply', 'donotreply', 'noreplies', 'notreply', 'noresponse',
  'postmaster', 'hostmaster', 'webmaster', 'mailerdaemon', 'abuse', 'spam', 'bounce', 'bounces',
  'unsubscribe', 'null', 'devnull', 'root', 'nobody',
]);

/**
 * Throwaway inbox providers. A short list of the ones people actually type at a counter or into a form, kept here
 * so there's no call out to a list service. Subdomains match too.
 */
export const DISPOSABLE_DOMAINS: ReadonlySet<string> = new Set([
  '10minutemail.com', '10minutemail.net', '20minutemail.com', '33mail.com', 'anonaddy.me', 'burnermail.io', 'byom.de',
  'discard.email', 'discardmail.com', 'dispostable.com', 'dropmail.me', 'emailondeck.com', 'fakeinbox.com', 'fakemail.net',
  'getairmail.com', 'getnada.com', 'grr.la', 'guerrillamail.biz', 'guerrillamail.com', 'guerrillamail.de', 'guerrillamail.info',
  'guerrillamail.net', 'guerrillamail.org', 'guerrillamailblock.com', 'harakirimail.com', 'inboxbear.com', 'inboxkitten.com',
  'jetable.org', 'mail-temp.com', 'mailcatch.com', 'maildrop.cc', 'mailinator.com', 'mailinator.net', 'mailnesia.com',
  'mailnull.com', 'mailsac.com', 'minutemail.com', 'mintemail.com', 'moakt.com', 'mohmal.com', 'mytemp.email', 'mytrashmail.com',
  'nada.email', 'pokemail.net', 'sharklasers.com', 'spam4.me', 'spamgourmet.com', 'temp-mail.io', 'temp-mail.org', 'tempail.com',
  'tempinbox.com', 'tempmail.com', 'tempmail.net', 'tempmailo.com', 'tempr.email', 'throwawaymail.com', 'trash-mail.com',
  'trashmail.com', 'trashmail.de', 'trashmail.net', 'yopmail.com', 'yopmail.fr', 'yopmail.net',
]);

/** Domains set aside for examples and tests (RFC 2606, RFC 6761): mail to them goes nowhere. */
const RESERVED = /(^|\.)(example\.(com|net|org)|test|example|invalid|localhost|local)$/;

export function isDisposableDomain(domain: string): boolean {
  const labels = domain.toLowerCase().split('.');
  for (let i = 0; i < labels.length - 1; i++) if (DISPOSABLE_DOMAINS.has(labels.slice(i).join('.'))) return true;
  return false;
}

/**
 * The verdict for one address. `allowReserved` lets example.com style addresses through (local development, tests
 * and staging, where the capture mailer never sends); production refuses them as throwaway.
 */
export function classifyAddress(input: string, opts: { allowReserved?: boolean } = {}): { verdict: AddressVerdict; email: string | null } {
  const email = normaliseAddress(input);
  if (!email) return { verdict: 'bad_email', email: null };
  const at = email.lastIndexOf('@');
  const local = email.slice(0, at).replace(/\+.*$/, '').replace(/[._-]/g, '');
  const domain = email.slice(at + 1);
  if (NOBODY.has(local)) return { verdict: 'no_reply_address', email };
  if (isDisposableDomain(domain)) return { verdict: 'throwaway', email };
  if (!opts.allowReserved && RESERVED.test(domain)) return { verdict: 'throwaway', email };
  return { verdict: 'ok', email };
}
